import { EncodingOperation, EncoderToolType } from '../types/EncodingOperation';

export type ColorBlindnessType =
  | 'protanopia'
  | 'protanomaly'
  | 'deuteranopia'
  | 'deuteranomaly'
  | 'tritanopia'
  | 'tritanomaly'
  | 'normal';

interface ColorBlindnessMatrix {
  matrix: number[][];
  name: string;
  description: string;
  prevalence: string;
}

export class ColorBlindnessService {
  private static readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  private static readonly SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

  /**
   * Color blindness transformation matrices
   * Based on Brettel, Viénot and Mollon JPEG algorithm
   */
  private static readonly CVD_MATRICES: Record<ColorBlindnessType, ColorBlindnessMatrix> = {
    normal: {
      name: 'Normal Vision',
      description: 'No color vision deficiency',
      prevalence: '~92% of population',
      matrix: [
        [1, 0, 0],
        [0, 1, 0],
        [0, 0, 1]
      ]
    },
    protanopia: {
      name: 'Protanopia',
      description: 'Red-blind (complete loss of red cones)',
      prevalence: '~1% of males',
      matrix: [
        [0.567, 0.433, 0],
        [0.558, 0.442, 0],
        [0, 0.242, 0.758]
      ]
    },
    protanomaly: {
      name: 'Protanomaly',
      description: 'Red-weak (anomalous red cones)',
      prevalence: '~1% of males',
      matrix: [
        [0.817, 0.183, 0],
        [0.333, 0.667, 0],
        [0, 0.125, 0.875]
      ]
    },
    deuteranopia: {
      name: 'Deuteranopia',
      description: 'Green-blind (complete loss of green cones)',
      prevalence: '~1% of males',
      matrix: [
        [0.625, 0.375, 0],
        [0.7, 0.3, 0],
        [0, 0.3, 0.7]
      ]
    },
    deuteranomaly: {
      name: 'Deuteranomaly',
      description: 'Green-weak (anomalous green cones)',
      prevalence: '~5% of males',
      matrix: [
        [0.8, 0.2, 0],
        [0.258, 0.742, 0],
        [0, 0.142, 0.858]
      ]
    },
    tritanopia: {
      name: 'Tritanopia',
      description: 'Blue-blind (complete loss of blue cones)',
      prevalence: '~0.001% of population',
      matrix: [
        [0.95, 0.05, 0],
        [0, 0.433, 0.567],
        [0, 0.475, 0.525]
      ]
    },
    tritanomaly: {
      name: 'Tritanomaly',
      description: 'Blue-weak (anomalous blue cones)',
      prevalence: '~0.01% of population',
      matrix: [
        [0.967, 0.033, 0],
        [0, 0.733, 0.267],
        [0, 0.183, 0.817]
      ]
    }
  };

  /**
   * Simulate color blindness on an image
   */
  static async simulate(
    imageFile: File,
    cvdType: ColorBlindnessType
  ): Promise<EncodingOperation> {
    const operationId = this.generateOperationId();

    try {
      // Validation
      this.validateFile(imageFile);

      // Load image
      const imageData = await this.loadImage(imageFile);

      // Apply CVD transformation
      const simulatedData = this.applyCVDMatrix(imageData, cvdType);

      // Convert to blob
      const outputBlob = await this.imageDataToBlob(simulatedData, imageFile.type);

      return {
        id: operationId,
        toolType: EncoderToolType.COLOR_BLINDNESS_SIMULATOR,
        operationType: 'encode',
        inputData: imageFile,
        outputData: outputBlob as File,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          cvdType,
          cvdName: this.CVD_MATRICES[cvdType].name,
          originalSize: imageFile.size,
          outputSize: outputBlob.size,
          format: imageFile.type
        }
      };
    } catch (error) {
      return {
        id: operationId,
        toolType: EncoderToolType.COLOR_BLINDNESS_SIMULATOR,
        operationType: 'encode',
        inputData: imageFile,
        outputData: null,
        timestamp: new Date(),
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get information about a specific CVD type
   */
  static getCVDInfo(cvdType: ColorBlindnessType): ColorBlindnessMatrix {
    return this.CVD_MATRICES[cvdType];
  }

  /**
   * Get all available CVD types
   */
  static getAllCVDTypes(): ColorBlindnessType[] {
    return Object.keys(this.CVD_MATRICES) as ColorBlindnessType[];
  }

  /**
   * Validate uploaded file
   */
  private static validateFile(file: File): void {
    if (!file) {
      throw new Error('No file provided');
    }

    if (!this.SUPPORTED_FORMATS.includes(file.type)) {
      throw new Error(`Unsupported file format. Supported: ${this.SUPPORTED_FORMATS.join(', ')}`);
    }

    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error(`File size exceeds ${this.MAX_FILE_SIZE / 1024 / 1024}MB limit`);
    }
  }

  /**
   * Load image file to ImageData
   */
  private static async loadImage(file: File): Promise<ImageData> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }

          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          resolve(imageData);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Apply CVD transformation matrix to image data
   */
  private static applyCVDMatrix(imageData: ImageData, cvdType: ColorBlindnessType): ImageData {
    const { matrix } = this.CVD_MATRICES[cvdType];
    const data = imageData.data;
    const newImageData = new ImageData(
      new Uint8ClampedArray(data),
      imageData.width,
      imageData.height
    );

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] / 255;
      const g = data[i + 1] / 255;
      const b = data[i + 2] / 255;

      // Apply matrix transformation
      const newR = matrix[0][0] * r + matrix[0][1] * g + matrix[0][2] * b;
      const newG = matrix[1][0] * r + matrix[1][1] * g + matrix[1][2] * b;
      const newB = matrix[2][0] * r + matrix[2][1] * g + matrix[2][2] * b;

      newImageData.data[i] = Math.round(newR * 255);
      newImageData.data[i + 1] = Math.round(newG * 255);
      newImageData.data[i + 2] = Math.round(newB * 255);
      newImageData.data[i + 3] = data[i + 3]; // Alpha unchanged
    }

    return newImageData;
  }

  /**
   * Convert ImageData to Blob
   */
  private static async imageDataToBlob(imageData: ImageData, mimeType: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.putImageData(imageData, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from image data'));
        }
      }, mimeType, 0.95);
    });
  }

  /**
   * Generate unique operation ID
   */
  private static generateOperationId(): string {
    return `cvd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
